import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import { Button } from '../ui/button'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '../ui/sheet'
import { WheelPicker, WheelPickerWrapper } from '../ui/wheel-picker'
import type { WheelPickerOption } from '../ui/wheel-picker'
import { formatSeconds } from './custom-exercise-form'
import { cn } from '../../lib/utils'

const MINUTES: WheelPickerOption<number>[] = Array.from({ length: 60 }, (_, n) => ({
  value: n,
  label: `${n}m`,
}))
const SECONDS: WheelPickerOption<number>[] = Array.from({ length: 60 }, (_, n) => ({
  value: n,
  label: `${n}s`,
}))

/**
 * Two iOS-style wheels — minutes and seconds — over a single value in seconds.
 *
 * Everything downstream of here is seconds; the split exists because "3m 30s"
 * is how a rest is thought about and "210" is not. Fully controlled, so it can
 * sit inside a sheet of its own (DurationField) or inside someone else's dialog
 * (CountAndRestField) without either owning a second copy of the state.
 */
export function DurationWheels({
  seconds,
  onChange,
}: {
  seconds: number
  onChange: (seconds: number) => void
}) {
  const minutePart = Math.floor(seconds / 60)
  const secondPart = seconds % 60

  return (
    <WheelPickerWrapper>
      <WheelPicker
        value={minutePart}
        onValueChange={(value) => onChange(value * 60 + secondPart)}
        options={MINUTES}
      />
      <WheelPicker
        value={secondPart}
        onValueChange={(value) => onChange(minutePart * 60 + value)}
        options={SECONDS}
      />
    </WheelPickerWrapper>
  )
}

interface DurationFieldProps {
  /** Names the value on the pill and heads the sheet. */
  label: string
  /** Seconds, or null when nothing has been set yet. */
  value: number | null
  onChange: (seconds: number) => void
  /** Lowest value the wheels may produce — 1 where zero would be meaningless. */
  min?: number
  invalid?: boolean
  className?: string
  id?: string
  /**
   * Optional control of the sheet from outside — the pause strip opens it the
   * moment "timed" is picked, rather than making that two taps.
   */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Hides the pill, for callers that already have something to open it with. */
  hideTrigger?: boolean
}

/**
 * A duration behind a pill that reads it back, picked in a bottom sheet. The
 * wheels are a draft until confirmed, like the other wheel sheets here: opening
 * one and dismissing it decides nothing.
 */
export function DurationField({
  label,
  value,
  onChange,
  min = 0,
  invalid = false,
  className,
  id,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: DurationFieldProps) {
  const { t } = useTranslation()
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const [draft, setDraft] = useState(0)

  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = (next: boolean) => {
    if (controlledOpen == null) setUncontrolledOpen(next)
    onOpenChange?.(next)
  }

  // Every opening starts from what is actually saved, done while rendering so
  // the wheels never show a stale value for a frame.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setDraft(value ?? 0)
  }

  const tooShort = draft < min

  function confirm() {
    if (tooShort) return
    onChange(draft)
    setOpen(false)
  }

  return (
    <>
      {!hideTrigger && (
      <Button
        type="button"
        variant="outline"
        id={id}
        onClick={() => setOpen(true)}
        aria-label={label}
        aria-invalid={invalid || undefined}
        className={cn(
          'h-9 w-full justify-between font-normal',
          value == null && 'text-muted-foreground',
          invalid && 'border-destructive text-destructive',
          className
        )}
      >
        {value == null ? t('common.notSet', '—') : formatSeconds(value)}
        <ChevronDown className="size-4 opacity-50" aria-hidden />
      </Button>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="pb-8" aria-describedby={undefined}>
          <SheetHeader>
            <SheetTitle>{label}</SheetTitle>
          </SheetHeader>

          <div className="flex justify-center pt-4">
            <DurationWheels seconds={draft} onChange={setDraft} />
          </div>

          <p className="pt-3 text-center text-sm text-muted-foreground">
            {tooShort
              ? t('trainingDiary.customExercise.blocks.durationMin', 'At least {{seconds}}s', {
                  seconds: min,
                })
              : formatSeconds(draft)}
          </p>

          <SheetFooter className="pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="button" disabled={tooShort} onClick={confirm}>
              {t('common.ok', 'OK')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  )
}
